import type { Ctx } from '@/core/context';
import { bus } from '@/core/bus';
import { nextRandom } from '@/core/rng';
import { tr } from '@/i18n';

/**
 * v0.6 成長系統：經驗值 → 等級（稱號）→ 月薪；福利點 → 4F 福利社；食譜收集。
 * 數值都在 content/game.json 的 progression（企劃可以調）。
 *
 * 怎麼拿經驗：做任務的每一步、完成任務／每日任務、案件好結局、洗手、撿東西、通報、抓老鼠、收集食譜、聊天、下班。
 * 升級：月薪 +perLevel、福利點 +levelUp、有些等級送食譜卡或特別能力（perks）。
 * 抓到老鼠交給品保：月薪 +100／隻（rats.raise）。
 */
type Cfg = Ctx['c']['game']['progression'];

/** 從 lv 升到 lv+1 要多少經驗 */
export const xpToNext = (cfg: Cfg, lv: number) => Math.round(cfg.levelXp * Math.pow(cfg.levelGrowth, lv - 1));

export function levelInfo(cfg: Cfg, xp: number): { lv: number; into: number; need: number; title: string } {
  let lv = 1, rest = xp;
  while (lv < cfg.maxLevel && rest >= xpToNext(cfg, lv)) { rest -= xpToNext(cfg, lv); lv++; }
  return { lv, into: rest, need: lv >= cfg.maxLevel ? 0 : xpToNext(cfg, lv), title: titleOf(cfg, lv) };
}
export const titleOf = (cfg: Cfg, lv: number) => cfg.titles[Math.min(cfg.titles.length - 1, Math.floor((lv - 1) / 3))];
export const levelOf = (ctx: Ctx) => levelInfo(ctx.c.game.progression, ctx.profile.xp ?? 0).lv;
export const hasPerk = (ctx: Ctx, level: number) => levelOf(ctx) >= level;

/** 加經驗：key 是 progression.xp 的名稱（mission、rat……）或直接給數字 */
export function gainXp(ctx: Ctx, key: string | number, why?: string): number {
  const cfg = ctx.c.game.progression;
  const n = typeof key === 'number' ? key : cfg.xp[key] ?? 0;
  if (!n) return 0;
  const before = levelInfo(cfg, ctx.profile.xp ?? 0).lv;
  ctx.profile.xp = (ctx.profile.xp ?? 0) + n;
  if (ctx.day?.earn) ctx.day.earn.xp += n;
  const after = levelInfo(cfg, ctx.profile.xp).lv;
  for (let lv = before + 1; lv <= after; lv++) levelUp(ctx, lv);
  if (why && n >= 20) ctx.ui.toast(tr("✨ +{0} 經驗：{1}", n, why));
  bus.emit('stateChanged', undefined);
  return n;
}

export function gainPoints(ctx: Ctx, key: string | number, why?: string): number {
  const n = typeof key === 'number' ? key : ctx.c.game.progression.points[key] ?? 0;
  if (!n) return 0;
  ctx.profile.points = (ctx.profile.points ?? 0) + n;
  if (ctx.day?.earn) { ctx.day.earn.points += n; if (why) ctx.day.earn.notes.push(tr("🎟️ +{0} 福利點：{1}", n, why)); }
  return n;
}

/** 加薪（月薪） */
export function giveRaise(ctx: Ctx, amount: number, why: string): void {
  ctx.profile.salary = (ctx.profile.salary ?? ctx.c.game.progression.salary.base) + amount;
  ctx.profile.raises = [...(ctx.profile.raises ?? []), { day: ctx.profile.day, amount, why }];
  if (ctx.day?.earn) { ctx.day.earn.raise += amount; ctx.day.earn.notes.push(tr("💰 月薪 +${0}：{1}", amount, why)); }
  ctx.log(tr("加薪 ${0}：{1}（月薪 ${2}）", amount, why, ctx.profile.salary.toLocaleString()), 'good');
}

function levelUp(ctx: Ctx, lv: number): void {
  const cfg = ctx.c.game.progression;
  giveRaise(ctx, cfg.salary.perLevel, tr("升到 Lv {0}", lv));
  gainPoints(ctx, 'levelUp', tr("升到 Lv {0}", lv));
  const perk = cfg.perks.find(p => p.level === lv);
  const got: string[] = [];
  for (let i = 0; i < (perk?.recipeCards ?? 0); i++) { const id = unlockRandomRecipe(ctx, tr("升級獎勵"), true); if (id) got.push(ctx.c.menuById.get(id)!.name); }
  bus.emit('sfx', 'ok');
  const title = titleOf(cfg, lv);
  ctx.log(tr("🎉 升級！Lv {0}「{1}」", lv, title), 'good');
  setTimeout(() => ctx.ui.card({ name: tr("🎉 升級！Lv {0}", lv), role: tr("稱號：{0}", title), color: '#f2c84b',
    text: tr("做得好！你現在是「{0}」。", title),
    sections: [{ label: tr("升級獎勵"), items: [tr("月薪 +${0}（現在 ${1}）", cfg.salary.perLevel, ctx.profile.salary.toLocaleString()), tr("福利點 +{0}（現在 {1}）", cfg.points.levelUp ?? 0, ctx.profile.points),
      ...(perk ? [`🎁 ${perk.text}`] : []), ...(got.length ? [tr("📖 解鎖食譜：{0}", got.join(tr("、")))] : [])], tone: 'good' as const }],
    choices: [{ text: tr("太好了！"), tone: 'primary', fn: () => ctx.ui.close() }] }), 600);
}

// ------------------------------------------------------------------ 食譜收集
/** 解鎖一道成品食譜；回傳是不是新的 */
export function unlockRecipe(ctx: Ctx, menuId: string, how: string, quiet = false): boolean {
  const m = ctx.c.menuById.get(menuId); if (!m) return false;
  ctx.profile.recipes ??= [];
  if (ctx.profile.recipes.includes(menuId)) return false;
  ctx.profile.recipes.push(menuId);
  if (!ctx.profile.productsSeen.includes(m.line)) ctx.profile.productsSeen.push(m.line);
  gainXp(ctx, 'recipe');
  ctx.log(tr("📖 收集食譜：{0}（{1}）", m.name, how), 'good');
  if (ctx.day?.earn) ctx.day.earn.notes.push(tr("📖 食譜：{0}", m.name));
  if (!quiet) { bus.emit('sfx', 'item'); ctx.ui.toast(tr("📖 收集到食譜：{0} {1}（{2}/{3}）", m.icon, m.name, ctx.profile.recipes.length, ctx.c.menu.length)); }
  return true;
}
export function unlockRandomRecipe(ctx: Ctx, how: string, quiet = false): string | null {
  const locked = ctx.c.menu.filter(m => !(ctx.profile.recipes ?? []).includes(m.id));
  if (!locked.length) return null;
  const pick = locked[Math.floor((ctx.day ? nextRandom(ctx.day) : Math.random()) * locked.length)];
  unlockRecipe(ctx, pick.id, how, quiet);
  return pick.id;
}

/** 今天每條產線在做哪兩款（主款、換線款）：依天數輪替，每條線輪流上下市 */
export function menuToday(ctx: Ctx, line: string): [string, string] | null {
  const items = ctx.c.menu.filter(m => m.line === line);
  if (!items.length) return null;
  const k = ((ctx.day?.day ?? ctx.profile.day) - 1) * 2;
  return [items[k % items.length].id, items[(k + 1) % items.length].id];
}

// ------------------------------------------------------------------ 薪資單
export function paycheck(ctx: Ctx): { daily: number; overtime: number; bonus: number; total: number } {
  const cfg = ctx.c.game.progression.salary;
  const salary = ctx.profile.salary ?? cfg.base;
  const daily = Math.round(salary / cfg.workDays);
  const over = Math.max(0, ctx.day.clock - ctx.day.shiftEnd);
  const overtime = Math.round(daily / 8 / 60 * over * 1.34);
  const bonus = ctx.day.earn?.raise ?? 0;
  return { daily, overtime, bonus, total: daily + overtime };
}
